import { getCategoriesInternally } from "@api/categories";
import { getCouponDeals, getCouponDealsInternally } from "@api/deals";
import Breadcrumb from "@components/BreadCrumb";
import CategoryMenu from "@components/CategoryMenu";
import CouponBanner from "@components/Coupons/CouponBanner";
import CouponList from "@components/Coupons/CouponList";
import Footer from "@components/Footer";
import Header from "@components/Header";
import SeoHead from "@components/SeoHead";
import Select from "@components/_shared/Select";
import { useGenzoTracking } from "@context/tracking/genzo";
import { useTranslations } from "@context/translations";
import withSiteConfig, { getSiteConfig } from "@context/withSiteConfig";
import withTranslations, { getTranslations } from "@context/withTranslations";
import { getCouponPageCategoryClickEvent } from "@helpers/genzo/genzoEventActionsBuilder";
import { getCouponPageView } from "@helpers/genzo/genzoPageViewBuilders";
import clsx from "clsx";
import { GetServerSideProps, NextPage } from "next";
import router from "next/router";
import { useEffect, useState } from "react";
import useSWR from "swr";
import styles from "./coupon.module.scss";

interface CouponPageProps {
  categories: Array<Category>;
  categorySlug: string;
  selectedCategory: Category;
  siteConfig: SiteConfig;
  currentPage: number;
  dealCount: number;
}

const CouponPage: NextPage<CouponPageProps> = ({
  categories,
  categorySlug,
  selectedCategory,
  siteConfig,
  currentPage,
  dealCount,
}) => {
  const { isRtl, siteCode, locale } = siteConfig;
  const translate = useTranslations();
  const { trackPageView, trackEvent } = useGenzoTracking();
  const defaultBreadcrumb = {
    name: translate("coupons"),
    path: "/coupons/all-stores",
  };
  const [breadcrumbs, setBreadcrumbs] = useState<Array<BreadCrumb>>([
    defaultBreadcrumb,
  ]);

  const { data: response } = useSWR(
    ["couponDeals", siteCode, locale, categorySlug, currentPage],
    (
      _: string,
      siteCode: string,
      locale: string,
      categorySlug: string,
      currentPage: number
    ) => getCouponDeals(siteCode, locale, categorySlug, currentPage)
  );
  const { deals, pagination } = response || {};

  useEffect(() => {
    trackPageView(getCouponPageView(selectedCategory));

    const newBreadcrumbs: Array<BreadCrumb> = [
      defaultBreadcrumb,
      selectedCategory.name === "footer_all_stores"
        ? {
            name: translate("footer_all_stores"),
            path: "/coupons/all-stores",
          }
        : {
            name: selectedCategory.name,
            path: `/coupons/${selectedCategory.slug}`,
          },
    ];

    setBreadcrumbs(newBreadcrumbs);
  }, [selectedCategory]);

  const handleOnCategoryChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    categories: Array<Category>
  ): void => {
    const newSelectedCategoryId = e.target.value;
    const categoryIndex = categories.findIndex(
      (category) => category.id === newSelectedCategoryId
    );
    const category = categories[categoryIndex];
    trackEvent(getCouponPageCategoryClickEvent(category.id, categoryIndex));
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(`/coupons/${category.slug}`);
  };

  const handleOnPaginationChange = (newPage: number): void => {
    scrollTo({ top: 0, behavior: "smooth" });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push({
      pathname: `/coupons/${categorySlug}`,
      query: { page: newPage },
    });
  };

  const handleOnCategoryClick = (categoryId: string, index: number): void => {
    trackEvent(getCouponPageCategoryClickEvent(categoryId, index));
  };
  return (
    <>
      <SeoHead
        page="coupons"
        name={
          selectedCategory.name === "footer_all_stores"
            ? translate(selectedCategory.name)
            : selectedCategory.name
        }
        dealCount={dealCount}
      ></SeoHead>
      <Header />
      <main role="main" className={clsx(styles.container, isRtl && styles.rtl)}>
        <div className={styles.wrap}>
          {breadcrumbs.length > 0 && (
            <Breadcrumb breadcrumbs={breadcrumbs} isRtl={isRtl} />
          )}

          <div className={styles.couponBanner}>
            <CouponBanner />
          </div>

          <div className={clsx(styles.content)}>
            <aside className={clsx(styles.aside)}>
              <div
                className={styles.categoryList}
                data-testid="category-menu-container"
              >
                <CategoryMenu
                  basePath="coupons"
                  categories={categories}
                  activeCategorySlug={categorySlug}
                  title={translate("filter_by")}
                  onCategoryClick={handleOnCategoryClick}
                />
              </div>
            </aside>

            <div className={styles.mainContent}>
              <div className={styles.categorySelect}>
                <div className={styles.categorySelectLabel}>
                  {translate("filter_by")}
                </div>
                <Select
                  defaultValue={categorySlug}
                  isRtl={isRtl}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleOnCategoryChange(e, categories)
                  }
                >
                  {categories.map((c) => (
                    <option key={c.slug} value={c.id}>
                      {c.name === "footer_all_stores"
                        ? translate("footer_all_stores")
                        : c.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className={styles.couponListContainer}>
                <CouponList
                  deals={deals}
                  pagination={pagination}
                  onPaginationChange={handleOnPaginationChange}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const env = process.env.APP_ENV;
  const siteConfig = getSiteConfig(context, env);
  const translations = await getTranslations(siteConfig);
  const categorySlug = context.params?.categorySlug as string;
  const categoriesResponse = await getCategoriesInternally(siteConfig.locale);

  const allStore: Category = {
    id: "all-stores",
    name: "footer_all_stores",
    enName: "All Stores",
    slug: "all-stores",
  };

  if (!categoriesResponse.categories) {
    return {
      notFound: true,
    };
  }

  const selectedCategory: Category =
    categorySlug === allStore.slug
      ? allStore
      : categoriesResponse.categories.find(
          (category: Category) => category.slug === categorySlug
        );

  if (!selectedCategory) {
    return {
      notFound: true,
    };
  }

  const convertPageToNumber = (page: string | string[]): number => {
    return typeof page === "string" ? parseInt(page, 10) : 1;
  };

  const currentPage = convertPageToNumber(context.query.page);

  const couponDeals = await getCouponDealsInternally(
    siteConfig.siteCode,
    siteConfig.locale,
    selectedCategory.slug,
    currentPage
  );

  return {
    props: {
      categories: [allStore].concat(categoriesResponse.categories),
      categorySlug,
      selectedCategory,
      siteConfig,
      translations,
      dealCount: couponDeals.pagination.totalCount,
      currentPage,
    },
  };
};

export default withTranslations(withSiteConfig(CouponPage));
